import { ref, onMounted, onUnmounted } from 'vue'
import api from '@/api'
import { io } from 'socket.io-client'

export function useNotifications() {
  const newAppointmentsCount = ref(0)
  const lastCheckedDate = ref(localStorage.getItem('lastCheckedAppointments') || new Date().toISOString())
  const soundEnabled = ref(localStorage.getItem('notificationSound') !== 'false')
  let intervalId = null
  let socket = null

  // Son de notification
  const notificationSound = new Audio('/notification.mp3')

  const playNotificationSound = () => {
    if (soundEnabled.value) {
      notificationSound.play().catch(err => {
        console.log('Son de notification désactivé ou bloqué par le navigateur:', err)
      })
    }
  }

  const toggleSound = () => {
    soundEnabled.value = !soundEnabled.value
    localStorage.setItem('notificationSound', soundEnabled.value)
  }

  const showNotification = (count) => {
    playNotificationSound()

    // Notification navigateur (si autorisée)
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Nouveau rendez-vous !', {
        body: `${count} nouveau${count > 1 ? 'x' : ''} rendez-vous`,
        icon: '/logo.png',
        badge: '/logo.png'
      })
    }
  }

  const checkNewAppointments = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      // Récupérer les rendez-vous depuis la dernière vérification
      const response = await api.get('/api/appointment/rdvs-jour', {
        params: {
          date: new Date().toISOString().split('T')[0]
        }
      })

      if (response.data && response.data.rendezVous) {
        // Filtrer les rendez-vous créés après la dernière vérification
        const newRdvs = response.data.rendezVous.filter(rdv => {
          return new Date(rdv.createdAt || rdv.date_rdv) > new Date(lastCheckedDate.value)
        })

        const previousCount = newAppointmentsCount.value
        newAppointmentsCount.value = newRdvs.length

        // Jouer le son si de nouveaux rendez-vous sont arrivés
        if (newRdvs.length > previousCount && previousCount !== null) {
          showNotification(newRdvs.length)
        }

        console.log(`📅 ${newRdvs.length} nouveau(x) rendez-vous détecté(s)`)
      }
    } catch (error) {
      console.error('Erreur lors de la vérification des nouveaux rendez-vous:', error)
    }
  }

  const markAsChecked = () => {
    lastCheckedDate.value = new Date().toISOString()
    localStorage.setItem('lastCheckedAppointments', lastCheckedDate.value)
    newAppointmentsCount.value = 0
  }

  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission()
    }
  }

  // 🔌 Connexion WebSocket pour les notifications en temps réel
  const connectSocket = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const firmId = user.firm || user.firmId

      if (!firmId) {
        console.warn('⚠️ FirmId non trouvé, WebSocket non initialisé')
        return
      }

      // URL du backend WebSocket (même serveur que l'API)
      const socketUrl = 'https://bookmysalon-967a856b16b6.herokuapp.com'

      socket = io(socketUrl, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000
      })

      socket.on('connect', () => {
        console.log('🔌 Socket.io connecté:', socket.id)
        // S'authentifier avec le firmId
        socket.emit('authenticate', { firmId })
      })

      // Écouter les nouveaux rendez-vous en temps réel
      socket.on('newAppointment', (data) => {
        console.log('🔔 Nouveau rendez-vous reçu via WebSocket:', data)

        // Incrémenter le compteur
        newAppointmentsCount.value++

        // Afficher la notification
        showNotification(1)
      })

      socket.on('disconnect', () => {
        console.log('🔌 Socket.io déconnecté')
      })

      socket.on('connect_error', (error) => {
        console.error('❌ Erreur de connexion Socket.io:', error.message)
      })

    } catch (error) {
      console.error('❌ Erreur lors de l\'initialisation du WebSocket:', error)
    }
  }

  const disconnectSocket = () => {
    if (socket) {
      socket.disconnect()
      socket = null
      console.log('🔌 Socket.io déconnecté manuellement')
    }
  }

  const startPolling = () => {
    // Vérifier immédiatement
    checkNewAppointments()

    // Puis toutes les 2 minutes (fallback)
    intervalId = setInterval(checkNewAppointments, 120000)
  }

  const stopPolling = () => {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
  }

  onMounted(() => {
    // Connecter le WebSocket pour les notifications en temps réel
    connectSocket()

    // Démarrer le polling comme fallback
    startPolling()

    // Demander la permission pour les notifications navigateur
    requestNotificationPermission()
  })

  onUnmounted(() => {
    disconnectSocket()
    stopPolling()
  })

  return {
    newAppointmentsCount,
    soundEnabled,
    toggleSound,
    markAsChecked,
    checkNewAppointments
  }
}
